import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Input, Btn, Label } from "@/components/ui-kit";
import { formatApiError } from "@/lib/format";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const msg = formatApiError(err);
      // axios uses literal "Network Error" when the request fails before reaching the server.
      // Give the user something actionable instead of a cryptic two-word error.
      if (typeof msg === "string" && msg.toLowerCase().includes("network error")) {
        setError("Can't reach the server. Check your internet connection, disable any VPN / ad-blocker / privacy extensions, and try again.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 -left-40 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left — Branding */}
        <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-950 text-white">
          <img
            src="/kerboom-logo-white.png"
            alt="Kerboom Design"
            className="max-w-[220px] w-full h-auto object-contain"
            data-testid="login-brand-logo"
          />

          <div className="max-w-md">
            <h1 className="font-display text-5xl font-bold leading-[1.05] mb-6">
              Your studio.<br />
              <span className="text-amber-400">One clear</span> view.
            </h1>
            <p className="text-zinc-400 text-lg leading-relaxed">
              Quotes, projects and clients — organised the way a designer actually thinks.
            </p>
          </div>

          <p className="text-xs text-zinc-500">© Kerboom Design {new Date().getFullYear()}</p>
        </div>

        {/* Right — Form */}
        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <div className="lg:hidden flex items-center justify-center mb-8">
              <img
                src="/kerboom-logo.png"
                alt="Kerboom Design"
                className="max-w-[180px] w-full h-auto object-contain"
                data-testid="login-brand-logo-mobile"
              />
            </div>

            <h2 className="font-display text-3xl font-bold text-zinc-950 mb-1.5">Welcome back</h2>
            <p className="text-sm text-zinc-500 mb-8">Sign in to your studio dashboard.</p>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="paul@kerboom.design"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
              {error && (
                <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              <Btn type="submit" className="w-full !py-3" size="lg" disabled={loading} data-testid="login-submit-btn">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Sign in
              </Btn>
              <p className="text-center text-xs text-zinc-500 pt-2" data-testid="login-forgot">
                Forgot your password? Ask another team member to reset it for you in <strong>Admin → Users</strong>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
